import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
`;

const TitleBlock = styled.div``;

const Title = styled.h1`
  font-size: 32px;
  margin: 0 0 6px 0;
  color: ${({ theme }) => theme.color.primaryText};
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.color.secondaryText};
  text-decoration: none;
  font-size: 14px;
  margin-top: 6px;
  &:hover {
    color: #667eea;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.color.cardBackground};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: ${({ theme }) => theme.color.secondaryText};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.border};
  background: ${({ theme }) => theme.color.background};
  color: ${({ theme }) => theme.color.primaryText};
  outline: none;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.border};
  background: ${({ variant }) =>
    variant === 'primary' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'};
  color: ${({ variant, theme }) => (variant === 'primary' ? '#fff' : theme.color.primaryText)};
  cursor: pointer;
  font-weight: 600;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.secondaryText};
  margin-top: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  color: ${({ theme }) => theme.color.secondaryText};
  padding: 10px 6px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
`;

const Td = styled.td`
  padding: 10px 6px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  font-size: 13px;
  color: ${({ theme }) => theme.color.primaryText};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.primaryText};
  background: ${({ status }) => {
    if (status === 'completed') return 'rgba(34, 197, 94, 0.12)';
    if (status === 'rejected') return 'rgba(239, 68, 68, 0.12)';
    if (status === 'approved') return 'rgba(59, 130, 246, 0.12)';
    if (status === 'processing') return 'rgba(234, 179, 8, 0.12)';
    return 'rgba(148, 163, 184, 0.12)';
  }};
`;

const Reversements = () => {
  const { t, currentLanguage } = useTranslation();
  const user = useSelector((state) => state.user?.data);

  const [loading, setLoading] = useState(true);
  const [savingBank, setSavingBank] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [syncingRevenue, setSyncingRevenue] = useState(false);

  const [withdrawable, setWithdrawable] = useState(0);
  const [revenueTx, setRevenueTx] = useState([]);
  const [settings, setSettings] = useState({ minimum_payout_amount: 50, default_platform_fee_percentage: 15 });
  const [payouts, setPayouts] = useState([]);

  const [bankForm, setBankForm] = useState({
    beneficiaryName: '',
    iban: '',
    bic: '',
    bankName: '',
    country: '',
    currency: 'EUR',
  });

  const apiBase = ENV_CONFIG.API_BASE_URL;
  const minPayout = useMemo(() => parseFloat(settings.minimum_payout_amount) || 0, [settings]);
  const getLocale = () => (currentLanguage === 'en' ? 'en-GB' : 'fr-FR');

  const getStatusLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'completed') return t('payouts.status.completed', 'Terminé');
    if (s === 'failed') return t('payouts.status.failed', 'Échoué');
    if (s === 'rejected') return t('payouts.status.rejected', 'Rejeté');
    if (s === 'approved') return t('payouts.status.approved', 'Approuvé');
    if (s === 'processing') return t('payouts.status.processing', 'En cours');
    if (s === 'pending') return t('payouts.status.pending', 'En attente');
    return status;
  };

  const revenueTotals = useMemo(() => {
    const eligible = (revenueTx || []).filter((r) => String(r?.status || '').toLowerCase() === 'completed' && !r?.included_in_payout);
    const sum = (arr, key) =>
      arr.reduce((acc, r) => {
        const raw = r?.[key];
        const n = typeof raw === 'string' ? parseFloat(raw.replace(',', '.')) : Number(raw);
        return Number.isFinite(n) ? acc + n : acc;
      }, 0);
    return {
      gross: +sum(eligible, 'amount').toFixed(2),
      platformFee: +sum(eligible, 'platform_fee').toFixed(2),
      net: +sum(eligible, 'organizer_share').toFixed(2),
      count: eligible.length
    };
  }, [revenueTx]);

  const fetchAll = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [settingsRes, payoutsRes, revenueRes] = await Promise.all([
        axios.get(`${apiBase}/api/wallet/payout/settings`, { withCredentials: true, timeout: 8000 }),
        axios.get(`${apiBase}/api/wallet/payouts/${user.id}`, { withCredentials: true, timeout: 8000 }),
        axios.get(`${apiBase}/api/wallet/revenue/withdrawable/${user.id}`, { withCredentials: true, timeout: 8000 }),
      ]);

      setSettings(settingsRes?.data || settings);
      setPayouts(payoutsRes?.data?.payouts || []);
      setWithdrawable(parseFloat(revenueRes?.data?.withdrawable_total) || 0);
      setRevenueTx(revenueRes?.data?.transactions || []);

      // bank info is optional; 404 is OK
      try {
        const bankRes = await axios.get(`${apiBase}/api/wallet/bank-account/${user.id}`, { withCredentials: true, timeout: 8000 });
        const existing = bankRes?.data;
        setBankForm((prev) => ({
          ...prev,
          beneficiaryName: existing?.beneficiary_name || '',
          // We do not get raw IBAN back (masked only), user must re-enter to change.
          iban: '',
          bic: existing?.bic || '',
          bankName: existing?.bank_name || '',
          country: existing?.country || '',
          currency: existing?.currency || 'EUR',
        }));
      } catch (e) {
        // ignore 404
      }
    } catch (e) {
      console.error(e);
      toast.error(t('payouts.loadError', 'Erreur lors du chargement des reversements'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveBank = async () => {
    if (!user?.id) return;
    if (!bankForm.beneficiaryName || !bankForm.iban) {
      toast.error(t('payouts.bankMissingFields', 'Veuillez remplir le nom du bénéficiaire et l’IBAN'));
      return;
    }
    setSavingBank(true);
    try {
      await axios.post(`${apiBase}/api/wallet/bank-account`, {
        userId: String(user.id),
        beneficiaryName: bankForm.beneficiaryName,
        iban: bankForm.iban,
        bic: bankForm.bic,
        bankName: bankForm.bankName,
        country: bankForm.country,
        currency: bankForm.currency,
      }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });

      toast.success(t('payouts.bankSaved', 'Informations bancaires enregistrées'));
      setBankForm((prev) => ({ ...prev, iban: '' })); // clear after save
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || t('payouts.bankSaveError', 'Erreur lors de l’enregistrement'));
    } finally {
      setSavingBank(false);
    }
  };

  const requestPayout = async () => {
    if (!user?.id) return;
    setRequesting(true);
    try {
      await axios.post(`${apiBase}/api/wallet/payouts/request-from-revenue`, {
        userId: String(user.id),
      }, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });

      toast.success(t('payouts.requestSent', 'Demande de reversement envoyée'));
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.response?.data?.error || t('payouts.requestError', 'Erreur lors de la demande'));
    } finally {
      setRequesting(false);
    }
  };

  const syncRevenue = async () => {
    if (!user?.id) return;
    setSyncingRevenue(true);
    try {
      const res = await axios.post(`${apiBase}/api/wallet/revenue/sync-from-tickets`, {
        userId: String(user.id),
      }, { withCredentials: true, headers: { 'Content-Type': 'application/json' }, timeout: 20000 });

      const inserted = Number(res?.data?.inserted || 0);
      toast.success(
        inserted > 0
          ? t('payouts.syncRevenueDone', 'Revenus synchronisés')
          : t('payouts.syncRevenueNothing', 'Aucun revenu à synchroniser')
      );
      await fetchAll();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || t('payouts.syncRevenueError', 'Erreur lors de la synchronisation'));
    } finally {
      setSyncingRevenue(false);
    }
  };

  if (!user) {
    return (
      <PageContainer>
        <Title>{t('payouts.title', 'Reversements')}</Title>
        <Subtitle>{t('payouts.loginRequired', 'Veuillez vous connecter pour demander un reversement.')}</Subtitle>
      </PageContainer>
    );
  }

  const canRequest = !loading && withdrawable >= minPayout && withdrawable > 0;

  return (
    <PageContainer>
      <Header>
        <TitleBlock>
          <BackLink to="/gcoin-transactions">← {t('payouts.back', 'Retour')}</BackLink>
          <Title>{t('payouts.title', 'Reversements')}</Title>
          <Subtitle>{t('payouts.subtitle', 'Demandez un reversement de vos revenus (tickets, dons, etc.).')}</Subtitle>
        </TitleBlock>
      </Header>

      <Card>
        <strong>{t('payouts.withdrawableBalance', 'Solde retirable')}</strong>
        <InfoLine>
          <span>{t('payouts.available', 'Disponible')}</span>
          <span>{loading ? '...' : `${withdrawable} GCOIN`}</span>
        </InfoLine>
        <InfoLine>
          <span>{t('payouts.revenueCount', 'Transactions')}</span>
          <span>{loading ? '...' : revenueTotals.count}</span>
        </InfoLine>
        <InfoLine>
          <span>{t('payouts.grossAmount', 'Montant')}</span>
          <span>{loading ? '...' : `${revenueTotals.gross} GCOIN`}</span>
        </InfoLine>
        <InfoLine>
          <span>{t('payouts.platformFee', 'Frais plateforme')}</span>
          <span>{loading ? '...' : `${revenueTotals.platformFee} GCOIN`}</span>
        </InfoLine>
        <InfoLine>
          <span>{t('payouts.netAmount', 'Net')}</span>
          <span>{loading ? '...' : `${revenueTotals.net} GCOIN`}</span>
        </InfoLine>
        <InfoLine>
          <span>{t('payouts.minimum', 'Minimum')}</span>
          <span>{minPayout} GCOIN</span>
        </InfoLine>
      </Card>

      <Card>
        <strong>{t('payouts.bankInfo', 'Informations bancaires')}</strong>
        <InfoLine>
          <span>{t('payouts.note', 'Note')}</span>
          <span>{t('payouts.ibanNote', 'Pour changer l’IBAN, merci de le re-saisir.')}</span>
        </InfoLine>
        <Row style={{ marginTop: 12 }}>
          <Field>
            <Label>{t('payouts.beneficiaryName', 'Nom du bénéficiaire *')}</Label>
            <Input value={bankForm.beneficiaryName} onChange={(e) => setBankForm((p) => ({ ...p, beneficiaryName: e.target.value }))} />
          </Field>
          <Field>
            <Label>{t('payouts.iban', 'IBAN *')}</Label>
            <Input value={bankForm.iban} onChange={(e) => setBankForm((p) => ({ ...p, iban: e.target.value }))} placeholder="FR76..." />
          </Field>
          <Field>
            <Label>{t('payouts.bic', 'BIC / SWIFT')}</Label>
            <Input value={bankForm.bic} onChange={(e) => setBankForm((p) => ({ ...p, bic: e.target.value }))} />
          </Field>
          <Field>
            <Label>{t('payouts.bank', 'Banque')}</Label>
            <Input value={bankForm.bankName} onChange={(e) => setBankForm((p) => ({ ...p, bankName: e.target.value }))} />
          </Field>
          <Field>
            <Label>{t('payouts.country', 'Pays (2 lettres)')}</Label>
            <Input value={bankForm.country} onChange={(e) => setBankForm((p) => ({ ...p, country: e.target.value.toUpperCase() }))} placeholder="FR" />
          </Field>
          <Field>
            <Label>{t('payouts.currency', 'Devise')}</Label>
            <Input value={bankForm.currency} onChange={(e) => setBankForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))} placeholder="EUR" />
          </Field>
        </Row>
        <ButtonRow>
          <Button variant="primary" onClick={saveBank} disabled={savingBank}>
            {savingBank ? t('payouts.saving', 'Enregistrement…') : t('payouts.save', 'Enregistrer')}
          </Button>
        </ButtonRow>
      </Card>

      <Card>
        <strong>{t('payouts.requestPayout', 'Demander un reversement')}</strong>
        <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 14 }}>
          {t('payouts.requestAllInfo', 'La demande gèle toutes vos transactions de revenus non encore incluses dans un reversement.')}
        </div>
        <ButtonRow>
          <Button variant="primary" onClick={requestPayout} disabled={requesting || !canRequest}>
            {requesting ? t('payouts.sending', 'Envoi…') : t('payouts.sendRequest', 'Envoyer la demande')}
          </Button>
        </ButtonRow>
        {!loading && !canRequest && (
          <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 13 }}>
            {withdrawable <= 0
              ? t('payouts.noWithdrawable', 'Aucun solde retirable pour le moment.')
              : t('payouts.belowMin', 'Solde retirable inférieur au minimum requis.')}
          </div>
        )}
      </Card>

      <Card>
        <strong>{t('payouts.revenueHistory', 'Mes revenus')}</strong>
        <ButtonRow style={{ marginTop: 10, justifyContent: 'flex-start' }}>
          <Button onClick={syncRevenue} disabled={syncingRevenue || loading}>
            {syncingRevenue ? t('payouts.syncingRevenue', 'Synchronisation…') : t('payouts.syncRevenue', 'Synchroniser mes revenus')}
          </Button>
        </ButtonRow>
        {(!revenueTx || revenueTx.length === 0) ? (
          <div style={{ marginTop: 10, color: '#94a3b8' }}>{t('payouts.noRevenueYet', 'Aucune transaction de revenu pour le moment.')}</div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('payouts.reference', 'Référence')}</Th>
                <Th>{t('payouts.grossAmount', 'Montant')}</Th>
                <Th>{t('payouts.platformFee', 'Frais plateforme')}</Th>
                <Th>{t('payouts.netAmount', 'Net')}</Th>
                <Th>{t('payouts.status', 'Statut')}</Th>
                <Th>{t('payouts.date', 'Date')}</Th>
              </tr>
            </thead>
            <tbody>
              {revenueTx.map((r) => (
                <tr key={r.id}>
                  <Td>{r.reference}</Td>
                  <Td>{r.amount}</Td>
                  <Td>{r.platform_fee}</Td>
                  <Td>{r.organizer_share}</Td>
                  <Td>
                    {r.included_in_payout
                      ? <Badge status="completed">{t('payouts.included', 'Inclus')}</Badge>
                      : <Badge status={String(r.status || '').toLowerCase()}>{String(r.status || '')}</Badge>}
                  </Td>
                  <Td>{r.created_at ? new Date(r.created_at).toLocaleString(getLocale()) : ''}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <strong>{t('payouts.myRequests', 'Mes demandes')}</strong>
        {(!payouts || payouts.length === 0) ? (
          <div style={{ marginTop: 10, color: '#94a3b8' }}>{t('payouts.noneYet', 'Aucune demande pour le moment.')}</div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('payouts.reference', 'Référence')}</Th>
                <Th>{t('payouts.grossAmount', 'Montant')}</Th>
                <Th>{t('payouts.netAmount', 'Net')}</Th>
                <Th>{t('payouts.status', 'Statut')}</Th>
                <Th>{t('payouts.date', 'Dates')}</Th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id}>
                  <Td>{p.reference}</Td>
                  <Td>{p.gross_amount}</Td>
                  <Td>{p.net_amount}</Td>
                  <Td><Badge status={p.status}>{getStatusLabel(p.status)}</Badge></Td>
                  <Td>
                    {p.requested_at ? new Date(p.requested_at).toLocaleString(getLocale()) : ''}
                    {p.approved_at ? ` • ${t('payouts.approvedAt', 'Approuvé')}: ${new Date(p.approved_at).toLocaleString(getLocale())}` : ''}
                    {p.processed_at ? ` • ${t('payouts.processedAt', 'Traitement')}: ${new Date(p.processed_at).toLocaleString(getLocale())}` : ''}
                    {p.completed_at ? ` • ${t('payouts.completedAt', 'Terminé')}: ${new Date(p.completed_at).toLocaleString(getLocale())}` : ''}
                    {p.rejection_reason ? ` • ${t('payouts.rejectionReason', 'Raison')}: ${p.rejection_reason}` : ''}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </PageContainer>
  );
};

export default Reversements;


